/**
 * Synced graph panel rendered next to the map when the user opens the
 * "support view" from the map toolbar. Renders the **same object set** as
 * the active investigation / exploration (read straight from the
 * investigations store) — no API round-trip, no separate scope.
 *
 * Mirrors the toolset of `InvestigationCanvas` so the support view feels
 * like the same Graph lens: layout selector, node search, and the entity-
 * type filter panel are all wired. Search and filter state are local; they
 * don't leak into the main Graph view's persisted exclusion store.
 *
 * Selection is bidirectional: the parent maps map-entity ↔ graph-node and
 * pumps it through `externalSelectedNodeId` / `onNodeSelected`.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { ArrowUpRight, Network } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { CytoscapeGraphRef } from '@/components/neo4j/cytoscape/CytoscapeGraph'
import CytoscapeGraph from '@/components/neo4j/cytoscape/CytoscapeGraph'
import type { GraphEdge, GraphNode, SelectedNode } from '@/components/neo4j/cytoscape/types'
import { ExploreFilterPanel } from '@/components/neo4j/explore-filter-panel'
import { useGraphExclusion } from '@/hooks/useGraphExclusion'
import { useInvestigationsStore } from '@/stores/investigations-store-provider'

type LayoutId =
  | 'fcose'
  | 'cola'
  | 'cose'
  | 'grid'
  | 'circle'
  | 'breadthfirst'
  | 'concentric'
  | 'clusters'
  | 'custom'

interface MapSupportGraphProps {
  caseId: string
  /** Node id to highlight (driven by the map's current selection). */
  externalSelectedNodeId: string | null
  /** Called once after the graph has reacted to an external selection so
   *  the parent can reset its trigger. */
  onExternalNodeHandled: () => void
  /** Bumped by the parent when the map deselects — clears graph selection. */
  externalClearSelectionSignal: number
  /** Fired when the user clicks a node in the graph. */
  onNodeSelected: (node: SelectedNode | null) => void
}

export function MapSupportGraph({
  caseId,
  externalSelectedNodeId,
  onExternalNodeHandled,
  externalClearSelectionSignal,
  onNodeSelected,
}: MapSupportGraphProps) {
  const { t } = useTranslation()
  const transient = useInvestigationsStore(s => s.transient)
  const activeId = useInvestigationsStore(s => s.activeId)
  const active = useInvestigationsStore(s =>
    activeId ? (s.saved.find(i => i.id === activeId) ?? null) : null
  )
  // Preserve the active investigation across the jump so the full Graph view
  // lands on the same object set without re-picking it from the chip bar.
  const fullGraphHref = activeId
    ? `/cases/${caseId}/graph?investigation=${activeId}`
    : `/cases/${caseId}/graph`

  const view = transient?.graphView ?? active?.graphView ?? null
  // Force a fresh cytoscape mount when the underlying viewpoint switches
  // — saves us from cross-pollinating layout / selection state between
  // unrelated investigations.
  const canvasKey = transient?.fromInsightId ?? activeId ?? 'empty'

  // Render the entire investigation object set — that's the graph's whole
  // point, to give context beyond what's pinnable on the map. Clicks on
  // non-geo nodes simply won't translate to a map selection (handled by
  // the parent's `handleGraphNodeSelected` via the entity-key mapping).
  const baseNodes = useMemo<GraphNode[]>(() => {
    if (!view) return []
    return view.nodes.map(n => ({
      id: n.id,
      name: n.name ?? n.id,
      labels: n.labels,
      typeLabel: n.typeLabel ?? n.labels[0],
      properties: n.properties,
    }))
  }, [view])

  const baseEdges = useMemo<GraphEdge[]>(() => {
    if (!view) return []
    return view.edges.map(e => ({
      id: e.id,
      type: e.type,
      source: e.source,
      target: e.target,
      properties: e.properties,
    }))
  }, [view])

  // ── Filter (entity-type visibility) ───────────────────────────────────
  // Namespaced separately from the main Graph view so toggling here doesn't
  // surprise the user when they switch back to /graph. Local-to-map scope.
  const exclusionState = useGraphExclusion(`map-support-graph-${caseId}`)
  const [showFilterPanel, setShowFilterPanel] = useState(false)

  const visibleNodes = useMemo<GraphNode[]>(() => {
    const { hiddenEntityTypes, hiddenEntityIds } = exclusionState.exclusion
    if (hiddenEntityTypes.length === 0 && hiddenEntityIds.length === 0) return baseNodes
    const hiddenTypes = new Set(hiddenEntityTypes)
    const hiddenIds = new Set(hiddenEntityIds)
    return baseNodes.filter(n => !hiddenTypes.has(n.typeLabel || 'Unknown') && !hiddenIds.has(n.id))
  }, [baseNodes, exclusionState.exclusion])

  // Always drop dangling edges — saved investigations can carry edges whose
  // endpoint nodes were never persisted, and cytoscape throws on init when
  // an edge references a missing node, aborting the whole canvas mount.
  const visibleEdges = useMemo<GraphEdge[]>(() => {
    const visibleIds = new Set(visibleNodes.map(n => n.id))
    return baseEdges.filter(e => visibleIds.has(e.source) && visibleIds.has(e.target))
  }, [baseEdges, visibleNodes])

  // ── Layout selector ──────────────────────────────────────────────────
  const [layout, setLayout] = useState<LayoutId>('fcose')

  // ── Search ───────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return []
    return baseNodes
      .filter(n => {
        const name = (n.name ?? '').toLowerCase()
        const id = n.id.toLowerCase()
        const type = (n.typeLabel ?? '').toLowerCase()
        return name.includes(q) || id.includes(q) || type.includes(q)
      })
      .slice(0, 20)
      .map(n => ({
        id: n.id,
        name: n.name || n.id,
        typeLabel: n.typeLabel,
      }))
  }, [searchQuery, baseNodes])

  // ── Cross-selection sync ─────────────────────────────────────────────
  const graphRef = useRef<CytoscapeGraphRef>(null)

  useEffect(() => {
    if (!externalSelectedNodeId) return
    if (!visibleNodes.find(n => n.id === externalSelectedNodeId)) return
    graphRef.current?.selectNodeAndCenter(externalSelectedNodeId)
    onExternalNodeHandled()
  }, [externalSelectedNodeId, visibleNodes, onExternalNodeHandled])

  useEffect(() => {
    if (externalClearSelectionSignal === 0) return
    graphRef.current?.clearSelection()
  }, [externalClearSelectionSignal])

  const handleSelect = useCallback(
    (node: SelectedNode | null) => {
      onNodeSelected(node)
    },
    [onNodeSelected]
  )

  const handleSearchSelect = useCallback(
    (node: { id: string; name: string; typeLabel?: string }) => {
      graphRef.current?.selectNodeAndCenter(node.id)
      setSearchQuery('')
      setSearchOpen(false)
    },
    []
  )

  if (!view || baseNodes.length === 0) {
    return (
      <div className="relative h-full w-full">
        <div className="h-full flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
          No object set to display. Open an investigation or pick an insight first.
        </div>
        <FullGraphTeaser href={fullGraphHref} t={t} />
      </div>
    )
  }

  return (
    <div className="relative h-full w-full">
      <CytoscapeGraph
        ref={graphRef}
        key={canvasKey}
        caseId={caseId}
        nodes={visibleNodes}
        edges={visibleEdges}
        layout={layout}
        onLayoutChange={l => setLayout(l as LayoutId)}
        onNodeSelect={handleSelect}
        nodeSearchQuery={searchQuery}
        onNodeSearchChange={q => {
          setSearchQuery(q)
          setSearchOpen(true)
        }}
        nodeSearchResults={searchResults}
        isNodeSearchOpen={searchOpen && searchResults.length > 0}
        onSearchNodeSelect={handleSearchSelect}
        onClearSearch={() => {
          setSearchQuery('')
          setSearchOpen(false)
        }}
        onToggleFilter={() => setShowFilterPanel(prev => !prev)}
        showFilterPanel={showFilterPanel}
        fitOnLoad
        enableLOD
        enableWebGL
      />

      <AnimatePresence initial={false}>
        {showFilterPanel && (
          <motion.div
            key="map-support-filter-panel"
            initial={{ opacity: 0, x: 16, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 16, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.85 }}
            // Phone: the panel ends above the full-graph teaser chip (in the
            // 45% pane the chip would cover its last filter row), and it is a
            // column so the filter list takes the panel's whole width.
            className="absolute top-4 bottom-14 z-20 flex w-64 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-lg md:bottom-4 md:w-72 md:flex-row"
            style={{ right: 'calc(var(--graph-tools-right, 1rem) + 3.5rem)' }}
          >
            <ExploreFilterPanel
              nodes={baseNodes}
              exclusionState={exclusionState}
              onClose={() => setShowFilterPanel(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <FullGraphTeaser href={fullGraphHref} t={t} />
    </div>
  )
}

// Footer-pinned teaser that nudges the analyst to open the full Graph view
// when they want layout / filter / save / agent capabilities beyond what the
// synced support panel can offer. Pointer events are scoped to the chip so
// the rest of the graph stays interactive even when the chip floats over it.
function FullGraphTeaser({ href, t }: { href: string; t: (key: string) => string }) {
  return (
    <div className="pointer-events-none absolute bottom-3 left-3 right-3 z-20 flex justify-center">
      <Link
        to={href}
        className="pointer-events-auto group inline-flex items-center gap-2.5 rounded-full border border-border/60 bg-background/90 px-3 py-1.5 text-[11px] font-medium text-foreground shadow-md backdrop-blur-sm transition-colors hover:border-primary/60 hover:bg-background hover:text-primary"
      >
        <Network className="h-3.5 w-3.5 text-primary" />
        <span className="flex-1 truncate">{t('geo.supportGraph.teaser.body')}</span>
        <span className="inline-flex items-center gap-1 font-semibold text-primary">
          {t('geo.supportGraph.teaser.cta')}
          <ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </Link>
    </div>
  )
}
